'use client'

import React, { useId, useRef, useState } from 'react'

import { Box, chakra } from '@chakra-ui/react'

import { Button } from '../Button/Button'
import { Spinner } from '../Spinner/Spinner'
import { StatTile, StatTileGroup } from '../StatTile/StatTile'
import type { StatTileProps } from '../StatTile/StatTile'
import { Tooltip } from '../Tooltip/Tooltip'
import {
  ColorScheme,
  summaryPanelActionButtonStyles,
  summaryPanelActionsStyles,
  summaryPanelContentBodyStyles,
  summaryPanelContentHeaderStyles,
  summaryPanelContentStyles,
  summaryPanelDescriptionStyles,
  summaryPanelHeaderStyles,
  summaryPanelLoadingStyles,
  summaryPanelStyles,
  summaryPanelTitleGroupStyles,
  summaryPanelTitleStyles,
} from './SummaryPanel.styles'

export type { ColorScheme }

export type SummaryPanelTile = Omit<
  StatTileProps,
  'onClick' | 'isSelected' | 'colorScheme'
> & {
  key: string
}

export type SummaryPanelAction = {
  label: string
  onClick: () => void
  icon?: React.ReactNode
  isDisabled?: boolean
  isLoading?: boolean
  /** Why the action is disabled: a tooltip on hover or keyboard focus, and
   * the button's accessible description. With a reason the button disables
   * via `aria-disabled` so it stays focusable for keyboard and screen-reader
   * users; without one it uses the plain `disabled` attribute. */
  disabledReason?: string
}

export type SummaryPanelProps = {
  title: React.ReactNode
  /** Muted copy under the title (e.g. facility and time window). */
  description?: React.ReactNode
  /** Controls on the right of the header, before `secondaryAction` (e.g. a
   * facility and date picker). Shown while loading too. */
  headerActions?: React.ReactNode
  /** Secondary button on the right of the header (e.g. "Download report"). */
  secondaryAction?: SummaryPanelAction
  /** Selectable tiles; selecting one shows `children` below the row. */
  tiles: SummaryPanelTile[]
  /** Key of the selected tile, or null when none is selected. */
  selectedTileKey: string | null
  /** Called with the clicked tile, or with nulls when the selected tile is
   * clicked again to deselect it. */
  onTileSelect: (key: string | null, tile: SummaryPanelTile | null) => void
  /** Tile columns; defaults to one row. Extra tiles wrap onto new rows. */
  tileColumns?: number
  /** Replaces the tiles and content with a spinner. */
  isLoading?: boolean
  loadingLabel?: string
  /** Caps the content body's height; it scrolls past this. */
  contentMaxHeight?: string
  /** Drops the content body's padding, for content that runs edge to edge
   * (e.g. an embedded Table). */
  isContentFlush?: boolean
  /** Hides the selected tile's label above the content, for content with
   * its own header (e.g. a Table's column headers). The content keeps the
   * label as its accessible name. */
  isContentHeaderHidden?: boolean
  /** Content for the selected tile. Only rendered while a tile is selected. */
  children?: React.ReactNode
  colorScheme?: ColorScheme
}

/**
 * Inlaid summary layout: a title, description and secondary action over a row
 * of selectable stat tiles, which drive the content shown below them.
 * Selection is controlled; the consumer renders the content for the selected
 * tile as `children`.
 */
export const SummaryPanel = ({
  title,
  description,
  headerActions,
  secondaryAction,
  tiles,
  selectedTileKey,
  onTileSelect,
  tileColumns,
  isLoading = false,
  loadingLabel = 'Loading…',
  contentMaxHeight,
  isContentFlush = false,
  isContentHeaderHidden = false,
  children,
  colorScheme = 'dark',
}: SummaryPanelProps) => {
  const selectedTile = tiles.find((tile) => tile.key === selectedTileKey)
  // A disabled button emits no mouse events, so the wrapper Box tracks the
  // hover (and bubbled focus) that anchors the disabled-reason tooltip.
  const actionRef = useRef<HTMLDivElement>(null)
  const [isActionHovered, setIsActionHovered] = useState(false)
  const [isActionFocused, setIsActionFocused] = useState(false)
  const disabledReasonId = useId()
  const disabledReason =
    secondaryAction?.isDisabled && secondaryAction.disabledReason

  return (
    <Box as='section' {...summaryPanelStyles(colorScheme)}>
      <Box {...summaryPanelHeaderStyles}>
        <Box {...summaryPanelTitleGroupStyles}>
          <chakra.h2 {...summaryPanelTitleStyles(colorScheme)}>
            {title}
          </chakra.h2>
          {description && (
            <Box {...summaryPanelDescriptionStyles(colorScheme)}>
              {description}
            </Box>
          )}
        </Box>
        {(headerActions || secondaryAction) && (
          <Box {...summaryPanelActionsStyles}>
            {headerActions}
            {secondaryAction && (
              <>
                <Box
                  css={summaryPanelActionButtonStyles(colorScheme)}
                  onBlur={() => setIsActionFocused(false)}
                  onFocus={() => setIsActionFocused(true)}
                  onMouseEnter={() => setIsActionHovered(true)}
                  onMouseLeave={() => setIsActionHovered(false)}
                  ref={actionRef}
                >
                  <Button
                    colorScheme={colorScheme}
                    disabled={
                      disabledReason ? undefined : secondaryAction.isDisabled
                    }
                    isLoading={secondaryAction.isLoading}
                    leftIcon={secondaryAction.icon}
                    onClick={
                      secondaryAction.isDisabled
                        ? undefined
                        : secondaryAction.onClick
                    }
                    size='md'
                    variant='secondary'
                    // With a reason, aria-disabled (instead of `disabled`)
                    // keeps the button focusable so keyboard and
                    // screen-reader users can reach its description.
                    {...(disabledReason && {
                      'aria-describedby': disabledReasonId,
                      'aria-disabled': true,
                    })}
                  >
                    {secondaryAction.label}
                  </Button>
                </Box>
                {disabledReason && (
                  <chakra.span id={disabledReasonId} srOnly>
                    {disabledReason}
                  </chakra.span>
                )}
                {disabledReason && (isActionHovered || isActionFocused) && (
                  <Tooltip
                    anchorEl={actionRef.current}
                    colorScheme={colorScheme}
                    label={disabledReason}
                    placement='bottom'
                  />
                )}
              </>
            )}
          </Box>
        )}
      </Box>

      {isLoading ? (
        <Box aria-live='polite' {...summaryPanelLoadingStyles(colorScheme)}>
          <Spinner aria-label={loadingLabel} colorScheme={colorScheme} />
          <chakra.span>{loadingLabel}</chakra.span>
        </Box>
      ) : (
        <>
          <StatTileGroup colorScheme={colorScheme} columns={tileColumns}>
            {tiles.map(({ key, ...tile }) => {
              const isSelected = key === selectedTileKey
              return (
                <StatTile
                  {...tile}
                  colorScheme={colorScheme}
                  isSelected={isSelected}
                  key={key}
                  onClick={() =>
                    isSelected
                      ? onTileSelect(null, null)
                      : onTileSelect(key, { key, ...tile })
                  }
                />
              )
            })}
          </StatTileGroup>

          {selectedTile && (
            <Box
              aria-label={selectedTile.label}
              role='region'
              {...summaryPanelContentStyles(colorScheme)}
            >
              {!isContentHeaderHidden && (
                <Box {...summaryPanelContentHeaderStyles(colorScheme)}>
                  {selectedTile.label}
                </Box>
              )}
              <Box
                {...summaryPanelContentBodyStyles(
                  contentMaxHeight,
                  isContentFlush
                )}
              >
                {children}
              </Box>
            </Box>
          )}
        </>
      )}
    </Box>
  )
}
